import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CircleDollarSign, LetterText, ReceiptText } from "lucide-react";
import { createExpensePolicyClient } from "shared";
import { toast } from "sonner";
import type z from "zod";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useAppForm } from "@/hooks/useCreateForm";
export const Route = createFileRoute("/_authed/_org/policies/expense/$id/edit")(
  {
    component: RouteComponent,
  },
);

function RouteComponent() {
  const { id: policyId } = Route.useParams();
  const navigate = Route.useNavigate();

  const { queryClient, trpc } = Route.useRouteContext();

  const { mutate: editPolicy, isPending } = useMutation(
    trpc.expensePolicy.edit.mutationOptions({
      onError: (error) => {
        toast.error("Failed to edit policy", {
          description: error.message,
          position: "bottom-left",
        });
      },
      onSuccess: ({ name }) => {
        toast.success(`"${name}" successfully updated`);
        navigate({
          to: "/policies/expense",
        });
        form.reset();
        queryClient.invalidateQueries(trpc.expensePolicy.list.queryOptions());
      },
    }),
  );

  const { data: policy } = useSuspenseQuery(
    trpc.expensePolicy.get.queryOptions({
      policyId,
    }),
  );
  const {
    id: _id,
    organizationId: _organizationId,
    receiptCents,
    memoCents,
    status: _status,
    isDefault: _isDefault,
    ...rest
  } = policy;
  const form = useAppForm({
    defaultValues: {
      ...rest,
      memoCents: memoCents === null ? 0 : memoCents,
      receiptCents: receiptCents === null ? 0 : receiptCents,
      requireBudget: policy.requireBudget,
      requireMemo: policy.memoCents !== null,
      requireReceipt: policy.receiptCents !== null,
    } as z.infer<typeof createExpensePolicyClient>,
    onSubmit: ({ value }) => {
      editPolicy({
        ...value,
        memoCents: value.requireMemo ? value.memoCents : null,
        policyId,
        receiptCents: value.requireReceipt ? value.receiptCents : null,
      });
      // Only submit receipt/memo cents if the respective option is enabled
    },
    validators: {
      onSubmit: createExpensePolicyClient,
    },
  });
  return (
    <div className="flex h-full flex-col">
      <SheetHeader className="mb-4 border-b px-6 py-6 pb-4">
        <SheetTitle>Edit "{policy.name}"</SheetTitle>
        <SheetDescription>
          Editing this policy will only impact{" "}
          <span className="italic">future</span> transactions. Requirements for
          past transactions will not be changed.
        </SheetDescription>
      </SheetHeader>
      <form.AppForm>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          {/* Scrollable content area with fixed max height */}
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-2 pb-4">
            <div className="space-y-4">
              <form.AppField
                children={(field) => (
                  <field.TextField label="Name" required={true} />
                )}
                name="name"
              />
              <form.AppField
                children={(field) => (
                  <field.TextAreaField label="Description" required={false} />
                )}
                name="description"
              />
              <form.AppField
                children={(field) => (
                  <field.TextField
                    description="Describe what details should be included in transaction memos"
                    label="Memo Prompt"
                  />
                )}
                name="memoPrompt"
              />
              {/* <Toggle variant={"outline"}></Toggle> */}
              <div className="flex w-full flex-row space-x-2">
                <form.AppField
                  children={(field) => (
                    <field.ToggleField variant={"outline"}>
                      <CircleDollarSign />
                      Require Budget
                    </field.ToggleField>
                  )}
                  name="requireBudget"
                />

                <form.AppField
                  children={(field) => (
                    <field.ToggleField variant={"outline"}>
                      <LetterText />
                      Require Memo
                    </field.ToggleField>
                  )}
                  name="requireMemo"
                />
                <form.AppField
                  children={(field) => (
                    <field.ToggleField variant={"outline"}>
                      <ReceiptText />
                      Require Receipt
                    </field.ToggleField>
                  )}
                  name="requireReceipt"
                />
              </div>

              <form.Subscribe
                selector={(state) => state.values.requireMemo}
                children={(required) =>
                  required && (
                    <form.AppField
                      children={(field) => (
                        <field.MoneyField
                          description="Transactions below this value will not require a memo. Leave at $0.00 to require for all transactions."
                          descriptionStyle="below"
                          label="Memo Threshold"
                        />
                      )}
                      name="memoCents"
                    />
                  )
                }
              />
              <form.Subscribe
                selector={(state) => state.values.requireReceipt}
                children={(required) =>
                  required && (
                    <>
                      <form.AppField
                        children={(field) => (
                          <field.MoneyField
                            description="Transactions below this value will not require a memo. Leave at $0.00 to require for all transactions."
                            descriptionStyle="below"
                            label="Receipt Threshold"
                          />
                        )}
                        name="receiptCents"
                      />
                      <form.AppField
                        children={(field) => (
                          <field.CheckboxField
                            description="Do not require receipts for virtual card purchases. "
                            label="Exempt Virtual Card Transactions"
                          />
                        )}
                        name="excludeVirtualReceipt"
                      />
                    </>
                  )
                }
              />
            </div>
          </div>

          <div className="flex min-h-16 flex-row items-center justify-start space-x-4 border-t p-6">
            <form.ConditionalSubmitCancelButtons isPending={isPending} />
          </div>
        </form>
      </form.AppForm>
    </div>
  );
}
